import React from "react";
import {
  HiOutlineLightBulb,
  HiOutlineSparkles,
  HiOutlineTrendingUp,
  HiOutlineExclamation,
} from "react-icons/hi";
import { formatCurrency } from "../../../utils/formatCurrency";
import { useTransactionAnalytics } from "../hooks/useTransactionAnalytics";

export const SmartInsights: React.FC = () => {
  const { data } = useTransactionAnalytics();
  const insights = data?.insights || [];

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <HiOutlineSparkles className="text-indigo-500" />
          Smart Insights
        </h2>
        <span className="text-sm text-gray-500">Updated just now</span>
      </div>

      <div className="space-y-4">
        {insights.map((insight, index) => (
          <InsightCard key={index} {...insight} />
        ))}
      </div>
    </div>
  );
};

interface InsightCardProps {
  type: "trend" | "anomaly" | "opportunity";
  title: string;
  description: string;
  impact?: number;
  action?: string;
}

const InsightCard: React.FC<InsightCardProps> = ({
  type,
  title,
  description,
  impact,
  action,
}) => {
  const icons = {
    trend: HiOutlineTrendingUp,
    anomaly: HiOutlineExclamation,
    opportunity: HiOutlineLightBulb,
  };

  const Icon = icons[type];

  return (
    <div className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 dark:bg-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
      <div
        className={`
        p-2 rounded-full
        ${type === "trend" ? "bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400" : ""}
        ${type === "anomaly" ? "bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400" : ""}
        ${type === "opportunity" ? "bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400" : ""}
      `}
      >
        <Icon className="w-5 h-5" />
      </div>

      <div className="flex-1">
        <h3 className="font-medium mb-1">{title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {description}
        </p>

        {impact && (
          <div className="mt-2 text-sm font-medium">
            Potential impact: {formatCurrency(impact)}
          </div>
        )}

        {action && (
          <button className="mt-3 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300">
            {action} →
          </button>
        )}
      </div>
    </div>
  );
};
